import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Space, Select, Tag, Typography, Row, Col, message, Statistic, Progress, Modal, Form, Input, List, Empty, Table,
} from 'antd';
import { RiseOutlined, PlusOutlined, PlayCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { apiGet, apiPost } from '../services/api';

const { Title, Text } = Typography;

// 商务风格配色
const COLORS = {
  primary: '#1677ff',
  secondary: '#722ed1',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  // 兼容旧代码的别名
  cyan: '#1677ff',
  purple: '#722ed1',
  pink: '#ff4d4f',
  green: '#52c41a',
  orange: '#faad14',
  yellow: '#faad14',
  darkBg: '#f5f5f5',
  cardBg: '#ffffff',
  borderDark: '#f0f0f0',
};

const STAGE_OPTIONS = [
  { code: 'new', label: '新账号', color: COLORS.success, icon: '🌱', days: '0-7天' },
  { code: 'growing', label: '成长期', color: COLORS.primary, icon: '🌿', days: '7-30天' },
  { code: 'mature', label: '成熟期', color: COLORS.secondary, icon: '🌳', days: '30-90天' },
  { code: 'veteran', label: '老账号', color: COLORS.warning, icon: '🏆', days: '90+天' },
];

const TASK_TYPE_OPTIONS = [
  { code: 'complete_profile', label: '完善资料', reward: 30 }, { code: 'build_social', label: '建立社交', reward: 20 },
  { code: 'active_behavior', label: '活跃行为', reward: 40 }, { code: 'first_post', label: '首次发帖', reward: 10 },
  { code: 'first_follow', label: '首次关注', reward: 5 }, { code: 'daily_active', label: '每日活跃', reward: 15 },
];

interface NurtureTask {
  id: number; botId: number; botName?: string; taskType: string; currentValue: number; targetValue: number;
  rewardPoints: number; status: string; deadline: string; createTime: string; completedTime?: string;
}
interface NurtureStats { totalTasks: number; pendingTasks: number; completedTasks: number; inProgressTasks: number; }
interface StageDistribution { stage: string; count: number; }

// 简洁页面标题组件
const PageHeader: React.FC<{ icon: React.ReactNode; title: string; subtitle?: string; color?: string }> = ({ icon, title, subtitle, color = COLORS.primary }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
      <div style={{
        width: 48, height: 48,
        background: `${color}10`,
        borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 24, color: color,
      }}>{icon}</div>
      <div>
        <Title level={2} style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>{title}</Title>
        {subtitle && <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14 }}>{subtitle}</Text>}
      </div>
    </div>
    <div style={{
      width: 120, height: 3, marginLeft: 72,
      background: `linear-gradient(90deg, ${color}, ${color}66, transparent)`,
      borderRadius: 2,
    }} />
  </div>
);

// 简洁卡片组件
const SimpleCard: React.FC<{ title?: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode; style?: React.CSSProperties; accentColor?: string }> = ({ title, extra, children, style, accentColor = COLORS.primary }) => (
  <Card
    title={title ? <Text style={{ fontWeight: 600, fontSize: 16 }}>{title}</Text> : undefined}
    extra={extra}
    style={{
      background: COLORS.cardBg,
      border: `1px solid ${COLORS.borderDark}`,
      borderRadius: 12,
      overflow: 'hidden',
      ...style,
    }}
    styles={{
      header: title ? {
        borderBottom: `1px solid ${COLORS.borderDark}`,
        background: `${accentColor}08`,
      } : undefined,
      body: { padding: 24 },
    }}
  >
    {children}
  </Card>
);

// 简洁统计卡片
const StatCard: React.FC<{ title: string; value: number; color: string }> = ({ title, value, color }) => (
  <Card style={{
    background: COLORS.cardBg,
    border: `1px solid ${COLORS.borderDark}`,
    borderRadius: 12,
  }}>
    <Statistic
      title={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>{title}</Text>}
      value={value}
      valueStyle={{ color: color, fontWeight: 700 }}
    />
  </Card>
);

const AccountNurture: React.FC = () => {
  const [tasks, setTasks] = useState<NurtureTask[]>([]);
  const [stats, setStats] = useState<NurtureStats | null>(null);
  const [stageDistribution, setStageDistribution] = useState<StageDistribution[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterTaskType, setFilterTaskType] = useState<string>('');
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [form] = Form.useForm();

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterTaskType) params.append('taskType', filterTaskType);
      const data = await apiGet<any>(`/api/nurture/tasks?${params.toString()}`);
      setTasks(Array.isArray(data) ? data : (data?.data || []));
    } catch (e: any) { message.error('加载失败: ' + e.message); setTasks([]); }
    finally { setLoading(false); }
  }, [filterStatus, filterTaskType]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiGet<any>(`/api/nurture/stats`);
      setStats(data?.data || data);
    } catch (e) { console.error('加载统计失败', e); }
  }, []);

  const fetchStageDistribution = useCallback(async () => {
    try {
      const data = await apiGet<any>(`/api/nurture/stage-distribution`);
      setStageDistribution(Array.isArray(data) ? data : (data?.data || []));
    } catch (e) { setStageDistribution([]); }
  }, []);

  useEffect(() => { fetchTasks(); fetchStats(); fetchStageDistribution(); }, [fetchTasks, fetchStats, fetchStageDistribution]);

  const handleBatchCreate = async (values: any) => {
    const botIds = values.botIds.split(',').map((id: string) => parseInt(id.trim())).filter((id: number) => !isNaN(id));
    if (botIds.length === 0) { message.warning('请输入有效的机器人ID'); return; }
    try {
      const data = await apiPost<any>(`/api/nurture/tasks/batch`, { botIds, taskType: values.taskType });
      message.success(`创建成功: ${data.created || 0} 个任务`);
      setShowBatchModal(false); form.resetFields(); fetchTasks(); fetchStats();
    } catch (e: any) { message.error('创建失败: ' + e.message); }
  };

  const handleExecuteTask = async (taskId: number) => {
    try { await apiPost<any>(`/api/nurture/task/${taskId}/execute`, {}); fetchTasks(); } catch (e: any) { message.error('执行失败: ' + e.message); }
  };

  const handleCompleteTask = async (taskId: number) => {
    try { await apiPost<any>(`/api/nurture/task/${taskId}/progress`, { progress: 100 }); fetchTasks(); fetchStats(); } catch (e: any) { message.error('更新失败: ' + e.message); }
  };

  const getTaskTypeLabel = (t: string) => TASK_TYPE_OPTIONS.find(o => o.code === t)?.label || t;
  const getStatusColor = (s: string) => ({ pending: '#6b7280', in_progress: COLORS.cyan, completed: COLORS.green, failed: COLORS.pink }[s] || '#6b7280');
  const getStatusLabel = (s: string) => ({ pending: '待执行', in_progress: '进行中', completed: '已完成', failed: '失败' }[s] || s);
  const getProgress = (t: NurtureTask) => t.targetValue === 0 ? 0 : Math.round((t.currentValue / t.targetValue) * 100);

  const limitsData = [
    { stage: '新账号', color: COLORS.green, post: 1, comment: 5, like: 20, follow: 10, maxFollow: 50 },
    { stage: '成长期', color: COLORS.cyan, post: 3, comment: 15, like: 50, follow: 30, maxFollow: 200 },
    { stage: '成熟期', color: COLORS.purple, post: 5, comment: 30, like: 100, follow: 50, maxFollow: 500 },
    { stage: '老账号', color: COLORS.orange, post: 10, comment: 50, like: 200, follow: 100, maxFollow: 1000 },
  ];

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      <PageHeader icon={<RiseOutlined />} title="账号养成" subtitle="管理机器人账号的成长阶段和养成任务" color={COLORS.yellow} />

      {/* 阶段分布 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {STAGE_OPTIONS.map(stage => {
          const dist = stageDistribution.find(d => d.stage === stage.code);
          return (
            <Col span={6} key={stage.code}>
              <Card style={{
                background: COLORS.cardBg,
                border: `1px solid ${stage.color}33`,
                borderRadius: 12,
              }}>
                <Space>
                  <div style={{
                    width: 48, height: 48,
                    background: `${stage.color}10`,
                    borderRadius: 12,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 24,
                  }}>{stage.icon}</div>
                  <div>
                    <Text style={{ fontWeight: 600, display: 'block' }}>{stage.label}</Text>
                    <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12 }}>{stage.days}</Text>
                    <Text style={{
                      color: stage.color,
                      fontWeight: 700,
                      display: 'block',
                    }}>{dist?.count || 0} 个账号</Text>
                  </div>
                </Space>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* 统计 */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}><StatCard title="总任务数" value={stats.totalTasks} color={COLORS.primary} /></Col>
          <Col span={6}><StatCard title="待执行" value={stats.pendingTasks} color="#6b7280" /></Col>
          <Col span={6}><StatCard title="进行中" value={stats.inProgressTasks} color={COLORS.primary} /></Col>
          <Col span={6}><StatCard title="已完成" value={stats.completedTasks} color={COLORS.success} /></Col>
        </Row>
      )}

      {/* 养成任务 */}
      <SimpleCard
        title="养成任务"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setShowBatchModal(true)} style={{ background: COLORS.warning, borderColor: COLORS.warning }}>批量创建任务</Button>}
        style={{ marginBottom: 24 }}
        accentColor={COLORS.warning}
      >
        <Space style={{ marginBottom: 16 }}>
          <Select value={filterStatus} onChange={setFilterStatus} style={{ width: 120 }} allowClear placeholder="全部状态" options={[{ value: '', label: '全部状态' }, { value: 'pending', label: '待执行' }, { value: 'in_progress', label: '进行中' }, { value: 'completed', label: '已完成' }]} />
          <Select value={filterTaskType} onChange={setFilterTaskType} style={{ width: 120 }} allowClear placeholder="全部类型" options={[{ value: '', label: '全部类型' }, ...TASK_TYPE_OPTIONS.map(o => ({ value: o.code, label: o.label }))]} />
          <Button onClick={fetchTasks} type="primary">查询</Button>
        </Space>
        <List loading={loading} dataSource={tasks} locale={{ emptyText: <Empty description={<Text style={{ color: '#6b7280' }}>暂无任务</Text>} /> }} renderItem={task => (
          <List.Item
            style={{
              padding: 20,
              marginBottom: 8,
              background: COLORS.darkBg,
              borderRadius: 12,
              border: `1px solid ${COLORS.borderDark}`,
            }}
            actions={[
              task.status === 'pending' && <Button icon={<PlayCircleOutlined />} onClick={() => handleExecuteTask(task.id)} type="primary">执行</Button>,
              task.status === 'in_progress' && <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => handleCompleteTask(task.id)} style={{ background: COLORS.success, borderColor: COLORS.success }}>完成</Button>,
            ].filter(Boolean)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, width: '100%' }}>
              <Tag style={{ background: `${COLORS.secondary}15`, border: `1px solid ${COLORS.secondary}33`, color: COLORS.secondary }}>{getTaskTypeLabel(task.taskType)}</Tag>
              <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>Bot#{task.botId}</Text>
              <div style={{ flex: 1 }}>
                <Space><Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>进度: <span style={{ color: COLORS.primary }}>{task.currentValue}</span> / {task.targetValue}</Text><Tag style={{ background: `${COLORS.success}15`, border: `1px solid ${COLORS.success}33`, color: COLORS.success }}>+{task.rewardPoints} 积分</Tag></Space>
                <Progress percent={getProgress(task)} size="small" strokeColor={{ '0%': COLORS.primary, '100%': COLORS.success }} trailColor={COLORS.borderDark} style={{ marginTop: 4 }} />
              </div>
              <Tag style={{ background: `${getStatusColor(task.status)}15`, border: `1px solid ${getStatusColor(task.status)}33`, color: getStatusColor(task.status) }}>{getStatusLabel(task.status)}</Tag>
            </div>
          </List.Item>
        )} />
      </SimpleCard>

      {/* 阶段行为限制 */}
      <SimpleCard title="阶段行为限制" accentColor={COLORS.warning}>
        <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 16, display: 'block' }}>不同阶段的账号有不同的行为限制，以模拟真实用户成长</Text>
        <Table
          dataSource={limitsData}
          rowKey="stage"
          pagination={false}
          columns={[
            { title: '阶段', dataIndex: 'stage', key: 'stage', render: (v: string, r: any) => <Space><span style={{ width: 12, height: 12, borderRadius: '50%', background: r.color, display: 'inline-block' }} /><Text>{v}</Text></Space> },
            { title: '日发帖', dataIndex: 'post', key: 'post', render: (v: number) => <Text>{v}</Text> },
            { title: '日评论', dataIndex: 'comment', key: 'comment', render: (v: number) => <Text>{v}</Text> },
            { title: '日点赞', dataIndex: 'like', key: 'like', render: (v: number) => <Text>{v}</Text> },
            { title: '日关注', dataIndex: 'follow', key: 'follow', render: (v: number) => <Text>{v}</Text> },
            { title: '最大关注', dataIndex: 'maxFollow', key: 'maxFollow', render: (v: number) => <Text>{v}</Text> },
          ]}
        />
      </SimpleCard>

      {/* 批量创建弹窗 */}
      <Modal title="批量创建养成任务" open={showBatchModal} onCancel={() => setShowBatchModal(false)} footer={null}>
        <Form form={form} onFinish={handleBatchCreate} layout="vertical" initialValues={{ taskType: 'complete_profile' }} style={{ marginTop: 24 }}>
          <Form.Item label={<Text style={{ color: '#8888aa' }}>任务类型</Text>} name="taskType" rules={[{ required: true }]}>
            <Select options={TASK_TYPE_OPTIONS.map(o => ({ value: o.code, label: `${o.label} (+${o.reward}积分)` }))} />
          </Form.Item>
          <Form.Item label={<Text style={{ color: '#8888aa' }}>机器人ID (多个用逗号分隔)</Text>} name="botIds" rules={[{ required: true }]}>
            <Input.TextArea placeholder="例如: 1,2,3,4,5" rows={3} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button onClick={() => setShowBatchModal(false)}>取消</Button>
              <Button type="primary" htmlType="submit" style={{ background: COLORS.warning, borderColor: COLORS.warning }}>创建</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AccountNurture;
